import React from "react";
import { Icon, Tooltip } from "@material-ui/core";
import styled, { css } from "styled-components";

import { Divider } from "js/Components/Divider";
import { t } from "js/i18n";

const FloatingContainer = styled.div`
    position: absolute;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    background: #ffffff;
    border: 1px solid #DDD;
    box-shadow: 0 9px 37px 0 rgba(0, 0, 0, 0.20);
    border-radius: 5px;
    padding: 5px 15px;
    gap: 10px;
    z-index: 1001;
`;

const TextElement = css`
    color: #444;
    font-family: "Source Sans Pro";
    font-size: 13px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
`;

const SelectedCountPill = styled.div`
    ${TextElement}
    padding: 5px 10px;
    white-space: nowrap;
    display: flex;
    align-items: center;
`;

const ActionButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    background: transparent;
    border: none;
    outline: none;
    cursor: pointer;
    padding: 5px 10px;
    white-space: nowrap;
    border-radius: 2px;
    ${TextElement}

    .MuiIcon-root {
        font-size: 18px;
        color: #888888;
    }

    &:hover {
        color: #11A9E2;
        background: rgba(255, 69, 0, 0.10);

        .MuiIcon-root {
            color: #11A9E2;
        }
    }

`;

const DeleteActionButton = styled(ActionButton)`
    &:hover {
        color: #FF4500;
        background: rgba(255, 69, 0, 0.10);

        .MuiIcon-root {
            color: #FF4500;
        }
    }

`;

const ClearButton = styled(ActionButton)`
    &&& {
       padding: 5px;
       border-radius: 20px;
    }
`;

const ActionIcon = styled(Icon).attrs({ className: "material-symbols-outlined" })`
    font-size: 18px;
    color:#888888;
`;

interface BulkActionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    icon?: string;
    children: React.ReactNode;
}

export function BulkActionButton({ icon, children, ...props }: BulkActionButtonProps) {
    return (
        <ActionButton type="button" {...props}>
            {icon && <ActionIcon>{icon}</ActionIcon>}
            {children}
        </ActionButton>
    );
}

export interface BulkActionBarProps {
    selectedCount: number;
    children?: React.ReactNode;
    onDelete?: () => void;
    onClearSelection?: () => void;
    disabled?: boolean;
}

export function BulkActionBar({
    selectedCount,
    children,
    onDelete,
    onClearSelection,
}: BulkActionBarProps) {
    // At least two items must be selected to show the bulk action bar.
    if (selectedCount < 2) {
        return null;
    }

    return (
        <FloatingContainer
            data-testid="bulk-action-bar"
            onMouseDown={event => {
                event.stopPropagation();
            }}
        >
            <SelectedCountPill data-testid="bulk-action-selected-count">
                {t("{{count}} Selected", { count: selectedCount })}
            </SelectedCountPill>

            {children && (
                <>
                    <Divider vertical margin={0} style={{ height: 18, backgroundColor: "#ddd" }} />
                    {children}
                </>
            )}

            {onDelete && (
                <>
                    <Divider vertical margin={0} style={{ height: 18, backgroundColor: "#ddd" }} />
                    <DeleteActionButton
                        data-testid="bulk-action-delete"
                        onClick={onDelete}
                    >
                        <ActionIcon>delete</ActionIcon>
                        {t("Delete")}
                    </DeleteActionButton>
                </>
            )}

            {onClearSelection && (
                <>
                    <Divider vertical margin={0} style={{ height: 18, backgroundColor: "#ddd" }} />
                    <Tooltip title={t("Clear Selection")}>
                        <ClearButton
                            type="button"
                            data-testid="bulk-action-clear-selection"
                            onClick={onClearSelection}
                        >
                            <ActionIcon>close</ActionIcon>
                        </ClearButton>
                    </Tooltip>
                </>
            )}
        </FloatingContainer>
    );
}

export default BulkActionBar;
